import { useEffect, useMemo, useState } from "react";

const IS_BROWSER = typeof window !== "undefined";

const SYSTEM_FONT_FAMILIES = new Set(
  [
    "Arial",
    "Helvetica",
    "Times New Roman",
    "Georgia",
    "Courier New",
    "Verdana",
    "Trebuchet MS",
    "Palatino",
    "Garamond",
    "Tahoma",
    "Lucida Sans",
    "Lucida Grande",
    "Lucida Console",
    "Gill Sans",
    "Impact",
    "Comic Sans MS",
    "system-ui",
  ].map((font) => font.toLowerCase())
);

const GENERIC_FONT_KEYWORDS = ["sans-serif", "serif", "monospace", "cursive", "fantasy"];

const GOOGLE_FONT_WEIGHTS = "300,400,500,600,700";
const GOOGLE_FONTS_ENDPOINT = "https://gwfh.mranftl.com/api/fonts";

const FALLBACK_GOOGLE_FONTS = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Poppins",
  "Montserrat",
  "Source Sans Pro",
  "Noto Sans",
  "Nunito",
  "Merriweather",
  "Playfair Display",
  "Oswald",
  "Raleway",
  "Rubik",
  "Work Sans",
  "Karla",
  "Cabin",
  "Barlow",
  "PT Sans",
  "PT Serif",
  "Space Grotesk",
  "Space Mono",
  "Fira Sans",
  "Fira Code",
  "Dancing Script",
  "Abril Fatface",
  "Bebas Neue",
  "Inconsolata",
  "Josefin Sans",
  "Kanit",
  "Libre Baskerville",
  "Lora",
  "Manrope",
  "Mulish",
  "Overpass",
  "Quicksand",
  "Roboto Slab",
  "Sora",
  "Titillium Web",
  "Ubuntu",
  "Varela Round",
  "Zilla Slab",
];

const loadedFonts = new Set();
let webFontLoaderPromise = null;
let cachedFonts = null;
let fontsFetchPromise = null;

const shouldSkipLoading = (fontFamily) => {
  const normalized = fontFamily.trim().toLowerCase();
  if (!normalized) return true;
  if (SYSTEM_FONT_FAMILIES.has(normalized)) return true;
  return GENERIC_FONT_KEYWORDS.some((keyword) => normalized.includes(keyword));
};

const toGoogleFontQuery = (fontFamily) => fontFamily.trim().replace(/\s+/g, "+");

const mergeFontLists = (fonts) => {
  const merged = new Set([...FALLBACK_GOOGLE_FONTS, ...fonts]);
  return Array.from(merged).sort((a, b) => a.localeCompare(b));
};

export function useGoogleFont(fontFamily) {
  useEffect(() => {
    if (!IS_BROWSER || !fontFamily) {
      return;
    }

    const baseFont = fontFamily.split(",")[0]?.trim();
    if (!baseFont || loadedFonts.has(baseFont)) {
      return;
    }

    if (shouldSkipLoading(baseFont)) {
      loadedFonts.add(baseFont);
      return;
    }

    let isMounted = true;

    const loadFont = async () => {
      try {
        if (!webFontLoaderPromise) {
          webFontLoaderPromise = import("webfontloader");
        }

        const webFontModule = await webFontLoaderPromise;
        const WebFont = webFontModule && "default" in webFontModule ? webFontModule.default : webFontModule;

        const googleQuery = toGoogleFontQuery(baseFont);

        WebFont.load({
          google: {
            families: [`${googleQuery}:${GOOGLE_FONT_WEIGHTS}`],
          },
          fontactive: (family) => {
            if (isMounted) {
              loadedFonts.add(family);
            }
          },
          fontinactive: (family) => {
            if (isMounted) {
              loadedFonts.add(family);
            }
            console.warn(`Warning: failed to load Google Font: ${family}`);
          },
        });
      } catch (error) {
        console.error("Warning: WebFont loader error:", error);
      }
    };

    void loadFont();

    return () => {
      isMounted = false;
    };
  }, [fontFamily]);
}

export function useGoogleFontsList() {
  const [fonts, setFonts] = useState(() => mergeFontLists([]));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!IS_BROWSER) return undefined;

    let isMounted = true;

    const loadFonts = async () => {
      if (cachedFonts) {
        if (isMounted) {
          setFonts(cachedFonts);
        }
        return;
      }

      if (!fontsFetchPromise) {
        if (isMounted) {
          setIsLoading(true);
        }

        fontsFetchPromise = fetch(GOOGLE_FONTS_ENDPOINT)
          .then(async (response) => {
            if (!response.ok) {
              throw new Error(`Unexpected response ${response.status}`);
            }

            const data = await response.json();
            const families = Array.isArray(data) ? data.map((font) => font.family).filter(Boolean) : [];
            cachedFonts = mergeFontLists(families);
            return cachedFonts;
          })
          .catch((error) => {
            console.error("Warning: failed to fetch Google Fonts list:", error);
            cachedFonts = mergeFontLists([]);
            return cachedFonts;
          })
          .finally(() => {
            fontsFetchPromise = null;
            if (isMounted) {
              setIsLoading(false);
            }
          });
      }

      try {
        const result = await fontsFetchPromise;
        if (isMounted && result) {
          setFonts(result);
        }
      } catch (error) {
        console.error("Warning: failed to resolve fonts fetch promise:", error);
      }
    };

    void loadFonts();

    return () => {
      isMounted = false;
    };
  }, []);

  const memoizedFonts = useMemo(() => fonts, [fonts]);

  return { fonts: memoizedFonts, isLoading };
}

